import { cva, type VariantProps } from 'class-variance-authority';
import { Loading03Icon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import { Slot } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export const buttonVariants = cva(
  [
    'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium',
    'transition-colors duration-[var(--ef-motion-fast)]',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    'disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress',
    '[&_svg]:size-[18px] [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand-primary text-text-on-primary hover:bg-brand-hover active:bg-brand-hover',
        secondary:
          'border border-border-subtle bg-surface-default text-text-primary hover:border-border-control hover:bg-surface-subtle active:bg-surface-subtle',
        ghost: 'text-text-primary hover:bg-surface-subtle active:bg-surface-subtle',
        danger: 'bg-danger text-text-on-primary hover:brightness-90 active:brightness-90',
        link: 'h-auto px-0 text-brand-primary underline underline-offset-4 hover:text-brand-hover',
      },
      size: {
        sm: 'h-8 px-3 text-meta',
        md: 'h-10 px-4 text-body',
        lg: 'h-11 px-5 text-body-lg',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Shows a spinner, sets aria-busy and disables the button. The label stays visible. */
  loading?: boolean;
  /** Render the single child element (e.g. a router Link) with button styles. */
  asChild?: boolean;
  /** Icon placed before the label (hidden while loading). Mark it aria-hidden. */
  leadingIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, asChild, leadingIcon, children, disabled, type, ...props },
  ref,
) {
  const classes = cn(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root ref={ref} className={classes} {...props}>
        {leadingIcon}
        <Slot.Slottable>{children}</Slot.Slottable>
      </Slot.Root>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Icon icon={Loading03Icon} aria-hidden className="animate-spin" /> : leadingIcon}
      {children}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'leadingIcon' | 'children' | 'asChild'> {
  /** Required accessible name (also shown as native tooltip). */
  label: string;
  icon: React.ReactNode;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, className, size = 'md', variant = 'ghost', loading, ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      aria-label={label}
      title={label}
      variant={variant}
      size={size}
      loading={loading}
      className={cn(size === 'sm' ? 'w-8 px-0' : size === 'lg' ? 'w-11 px-0' : 'w-10 px-0', className)}
      {...props}
    >
      {loading ? null : icon}
    </Button>
  );
});
